import React, { lazy, Suspense } from 'react'
import { AuthRoute, NotFound, Route, Switch, Redirect, useParams } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useUser } from '@fs/zion-user'
import { useCanAddPersonalData } from '@fs/zion-permissions'

import sites from './sites'
import { MemoryBookProvider } from './providers/MemoryBookProvider'
import { ChineseSurnameProvider } from './providers/ChineseSurnameProvider'

// Import site configs
import chineseConfig from './sites/chinese/chinese-config'
import PrintingPages from './pages/PrintingPages'

const EditorPage = lazy(() => import('./pages/EditorPage/EditorPage'))

// Wrapper component for EditorPage with providers
const WrappedEditorPage = (props) => (
  <ChineseSurnameProvider>
    <EditorPage {...props} />
  </ChineseSurnameProvider>
)

function WrappedPrintPage() {
  return (
    <ChineseSurnameProvider>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <PrintingPages />
      </div>
    </ChineseSurnameProvider>
  )
}

// Helper function to get config based on site name
const getSiteConfig = (siteName) => {
  switch (siteName) {
    case 'chinese':
      return chineseConfig

    default:
      return chineseConfig
  }
}

export default function App() {
  // The useCanAddPersonalData hook is used in conjuction with the requirePersonalData prop due to use wanting to show the landing page initially.
  const [canAddPersonalData] = useCanAddPersonalData()
  const { signedIn } = useUser()
  const canView = !signedIn || canAddPersonalData
  const { siteName } = useParams()
  const siteConfig = getSiteConfig(siteName)

  return (
    <ErrorBoundary>
      <MemoryBookProvider siteConfig={siteConfig} isAuthenticated={signedIn}>
        <Suspense>
          <Switch>
            <Route exact path="/" requirePersonalData={!canView}>
              <Redirect to="/chinese" />
            </Route>
            <Route exact path="/editor" requirePersonalData={!canView}>
              <Redirect to="/chinese/editor" />
            </Route>
            {Object.entries(sites).map(([path, component]) => {
              return (
                <Route
                  key={path}
                  component={component}
                  exact
                  path={`/:siteName(${path})`}
                  requirePersonalData={!canView}
                />
              )
            })}
            {Object.entries(sites).map(([path]) => {
              return (
                <AuthRoute
                  key={path}
                  component={WrappedEditorPage}
                  exact
                  path={`/:siteName(${path})/editor`}
                  requirePersonalData={!canView}
                />
              )
            })}
            {Object.entries(sites).map(([path]) => {
              return (
                <AuthRoute
                  key={path}
                  component={WrappedPrintPage}
                  exact
                  path={`/:siteName(${path})/print`}
                  requirePersonalData={!canView}
                />
              )
            })}
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </MemoryBookProvider>
    </ErrorBoundary>
  )
}
